"use strict";
import { Client } from "@/Common/Api/Client";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { CallCenterUserConnectionState } from "@/Common/Models/CallCenter/CallCenterUserConnectionState";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ClickToDialViewMode, IClickToDialProps, SendClickToDialRequest, SendClickToDialResponse } from "./ClickToDial.types";
import { ClickToDialViewHyperLink } from "./ClickToDialViewHyperLink";

interface IClickToDialViewProps extends IClickToDialProps {
    formattedPhoneNumber: string
}

function ClickToDialView(props: Readonly<IClickToDialViewProps>): JSX.Element {
    const { formattedPhoneNumber, onSuccessfulDial, viewMode, className } = props;
    const { getText } = useTranslation();

    const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();
    const isActive: boolean = sessionStore.CallCenter_UserConnectionState === CallCenterUserConnectionState.Connected;
    const providerType: string = sessionStore.CallCenter_Credentials?.Provider ?? null;

    function formatNumberForTelProtocol(number: string): string {
        // replace spaces with dashes if between two numbers, otherwise just remove the spaces, keeping other formatting characters
        const spacesBetweenStuff = /([^\s]|^)\s+([^\s]|$)/g;
        const isDigit = /^\d$/;
        const replacementFunc = (match: string, beforeMatch: string, afterMatch: string): string => {
            if (isDigit.test(beforeMatch) && isDigit.test(afterMatch)) {
                return `${beforeMatch}-${afterMatch}`;
            }
            return beforeMatch + afterMatch;
        };
        const spacesRemoved = number.replace(spacesBetweenStuff, replacementFunc);
        return `tel:${spacesRemoved}`;
    }

    // This is only used for the cases when CallCenter integration is not enabled, or supported, or not logged in, failed to log in, etc
    const phoneNumberWithProtocol: string = formatNumberForTelProtocol(formattedPhoneNumber);

    async function onClickToDial(): Promise<void> {
        if (!callCenterFeatureFlag) {
            return;
        }

        // prefer patientContactFacetId if both provided and get current patient's ContactFacetId if neither are provided
        const patientContactFacetId = props.patientContactFacetId
            ?? (props.patientContactId == null && props.forActivePatient
                ? GuidHelper.AsGuid(patientStore.getCurrentPatient()?.PatientResponse?.ContactFacet_id)
                : null
            );

        const patientContactId = patientContactFacetId == null
            ? (props.patientContactId ?? null)
            : null;

        const request: SendClickToDialRequest = {
            PhoneNumber: formattedPhoneNumber,
            PatientContactFacetId: patientContactFacetId,
            PatientContactId: patientContactId
        };
        const response = await Client.postAsync<SendClickToDialResponse>(ServerOperations.Server.Myle_CallCenter_ClickToDial, request);
        if (!response.isSuccess) {
            console.error("Error on ClickToDial", response);
            displayToast(MessageToast.Error, getText("CallCenter_ClickToDial_FailureToast"));
        }

        if (response.data.DialSuccess) {
            displayToast(MessageToast.Success, getText("CallCenter_ClickToDial_SuccessToast"));

            if (onSuccessfulDial) {
                onSuccessfulDial();
            }
        }
        else {
            displayToast(MessageToast.Error, getText("CallCenter_ClickToDial_NotSupportedToast"));
        }
    }

    if (formattedPhoneNumber == null || formattedPhoneNumber == "") {
        return <></>;
    }

    if (callCenterFeatureFlag && isActive) {
        if (viewMode == ClickToDialViewMode.Hyperlink) {
            return <ClickToDialViewHyperLink displayText={formattedPhoneNumber} providerType={providerType} onPhoneNumberClick={(onClickToDial)} className={className} />;
        }
    }

    return <a href={phoneNumberWithProtocol} target="_blank">{formattedPhoneNumber}</a>;
}

export { ClickToDialView };
